import React, { memo, forwardRef, useState } from 'react';
import { View, TextInput as RNTextInput } from 'react-native';
import {
  Text,
  Space,
  IconButton,
  IconClose,
  IconCrown,
} from '@redlink/components/atoms';
import { Button } from '@redlink/components/molecules';
import { createUseStyle } from '@redlink/assets/styles';
import { useMask } from './useMask';

export const TextInput = memo(
  forwardRef(
    (
      {
        style,
        title,
        prefix,
        postfix,
        mask,
        en = false,
        onFocus: onFocusOrigin,
        onBlur: onBlurOrigin,
        onChangeText: onChangeTextOrigin,
        onKeyPress: onKeyPressOrigin,
        editable = true,
        multiline,
        smallTextArea = false,
        value: originText = '',
        textStyle,
        require = false,
        withActionButton = false,
        actionButtonTitle = '',
        actionButtonVariant = 'actionBlue',
        actionButtonIcon,
        onActionButtonPress: onActionButtonPressOrigin,
        withClearButton = false,
        withCrownIcon = false,
        onClearButtonPress,
        description,
        ...restProps
      },
      ref
    ) => {
      const { styles, colors } = useStyle();
      const { getUpdatedValue, getRawValue } = useMask(mask);
      const [isFocus, setFocus] = useState(false);
      const [actionButtonIsFocus, setActipnButtonIsFocus] = useState(false);

      const onFocus = () => {
        editable && setFocus(true);
        onFocusOrigin && onFocusOrigin();
      };
      const onBlur = () => {
        onBlurOrigin && onBlurOrigin();
        setTimeout(() => {
          setFocus(false);
        }, 50);
      };
      const onKeyPress = ({ nativeEvent: { key: keyValue } }) => {
        if (keyValue == 'Enter' && onActionButtonPress) onActionButtonPress();
        onKeyPressOrigin && onKeyPressOrigin();
      };
      const onChangeText = (rawValue) => {
        const updatedValue = mask
          ? getUpdatedValue(rawValue, rawValue)
          : rawValue;
        const originValue = mask ? getRawValue(updatedValue) : rawValue;
        onChangeTextOrigin && onChangeTextOrigin(originValue, updatedValue);
      };

      const value = mask ? getUpdatedValue(originText, originText) : originText;

      const onActionButtonPress = () => {
        withActionButton &&
          onActionButtonPressOrigin &&
          onActionButtonPressOrigin();

        setTimeout(() => {
          ref?.current?.focus();
          setActipnButtonIsFocus(false);
          setFocus(true);
        }, 100);
      };

      return (
        <>
          <View
            style={[
              styles.container,
              style,
              {
                borderBottomColor: isFocus ? colors.blue1 : colors.gray21,
              },
            ]}
          >
            <Text
              style={[
                styles.titleText,
                {
                  color: isFocus ? colors.blue1 : colors.gray21,
                },
              ]}
            >
              {title}
              {require && <Text style={styles.requireText}>*</Text>}
            </Text>
            <Space size={4} />
            <View
              style={[styles.inputContainer, en && styles.inputContainerEn]}
            >
              {prefix && (
                <Text {...{ en }} style={styles.prefixText}>
                  {prefix}
                </Text>
              )}
              <RNTextInput
                {...{ ref }}
                {...restProps}
                style={[
                  styles.text,
                  textStyle,
                  !en && styles.textFontFamily,
                  !editable && styles.disbaledText,
                  multiline && multiline === 'small'
                    ? styles.multilineTextSmall
                    : multiline && styles.multilineText,
                ]}
                {...{
                  onFocus,
                  onBlur,
                  onChangeText,
                  editable,
                  multiline,
                  value,
                  onKeyPress,
                }}
              />
              {postfix && (
                <Text {...{ en }} style={styles.prefixText}>
                  {postfix}
                </Text>
              )}
              {withActionButton && (isFocus || actionButtonIsFocus) && (
                <>
                  <Space size={4} />
                  <Button
                    title={actionButtonTitle}
                    variant={actionButtonVariant}
                    onFocus={() => {
                      setActipnButtonIsFocus(true);
                    }}
                    onBlur={() => {
                      setFocus(false);
                      setActipnButtonIsFocus(false);
                    }}
                    onPress={onActionButtonPress}
                    icon={actionButtonIcon}
                    {...restProps}
                  />
                </>
              )}
              {withClearButton && (
                <>
                  <Space size={4} />
                  <IconButton
                    onPress={onClearButtonPress}
                    style={styles.clearButton}
                  >
                    <IconClose color={colors.white} size={15} />
                  </IconButton>
                </>
              )}
            </View>
          </View>
          {description && (
            <>
              <Space size={4} />
              <View style={styles.descriptionContainer}>
                {withCrownIcon && (
                  <>
                    <IconCrown />
                    <Space size={4} />
                  </>
                )}
                <Text style={styles.description}>{description}</Text>
              </View>
            </>
          )}
        </>
      );
    }
  )
);

export * from './preview';

const useStyle = createUseStyle(({ colors, language, ltrFlexDirection }) => ({
  container: {
    backgroundColor: colors.gray7,
    borderBottomWidth: 2,
    paddingBottom: 10,
    paddingTop: 3,
    paddingHorizontal: 9,
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
  },
  description: {
    fontSize: 12,
    color: colors.gray21,
  },
  text: {
    width: '100%',
    fontSize: 14,
    color: colors.black,
  },
  textFontFamily: {
    ...(language === 'fa' && { fontFamily: 'dana' }),
    fontSize: 16,
  },
  titleText: {
    fontSize: 10,
    color: colors.black,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inputContainerEn: {
    flexDirection: ltrFlexDirection,
  },
  prefixText: {
    color: colors.gray21,
    paddingEnd: 5,
  },
  disbaledText: {
    color: colors.gray22,
  },
  multilineText: {
    minHeight: 100,
  },
  multilineTextSmall: {
    minHeight: 50,
  },
  requireText: {
    color: colors.red1,
    fontSize: 10,
    marginStart: 5,
  },
  button: {
    marginHorizontal: 5,
    borderRadius: 4,
  },
  clearButton: {
    padding: 0,
    width: 15,
    height: 15,
    backgroundColor: colors.gray23,
    borderRadius: 50,
  },
  descriptionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));
